import React, { FormEvent, useCallback, useEffect, useState } from 'react'
import Form from 'react-bootstrap/Form'
import FormGroup from 'react-bootstrap/FormGroup'
import FormLabel from 'react-bootstrap/FormLabel'
import FormControl from 'react-bootstrap/FormControl'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import ModalHeader from 'react-bootstrap/ModalHeader'
import ModalTitle from 'react-bootstrap/ModalTitle'
import ModalBody from 'react-bootstrap/ModalBody'
import { useDispatch } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { uploadFormAPK, checkUploadAvailability } from 'app/services/client'
import { setIsLoading, setLoadingProgress } from 'app/state/appSlice'
import {
    makeTokenFileConfig,
    makeTokenHeaderConfig,
    useTokenHeaderConfig,
} from 'app/services/tokenConfig'
import { APKUploadViewProps } from './types'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import { handleServerAPIError } from 'app/services/utils'
import { UploadAvailability } from '../../../domain/UploadAvailability'

const formatBytes = (bytes?: number) => {
    if (bytes === undefined || Number.isNaN(bytes)) return '-'
    if (bytes === 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    const value = bytes / Math.pow(1024, i)
    return `${value.toFixed(i === 0 ? 0 : 2)} ${units[i]}`
}

export const APKUploadView = (props: APKUploadViewProps) => {
    const [file, setFile] = useState<File>()
    const [spaceInfo, setSpaceInfo] = useState<UploadAvailability | null>(null)
    const [spaceLoading, setSpaceLoading] = useState(false)

    const { appId, apkId, isOpen, toggle } = props
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [token] = useTokenHeaderConfig()

    useEffect(() => {
        if (!isOpen) {
            setFile(undefined)
            setSpaceInfo(null)
            setSpaceLoading(false)
        }
    }, [isOpen])

    const onUploadProgress = useCallback(
        (progressEvent: ProgressEvent) => {
            const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
            )
            dispatch(setLoadingProgress(percentCompleted))
        },
        [dispatch],
    )

    const onUpload = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()

            if (file && spaceInfo?.canUpload) {
                dispatch(setIsLoading(true))
                const formData = new FormData(event.currentTarget)
                formData.append('name', 'data')
                formData.append('fileName', file.name)
                formData.append('data', file)

                uploadFormAPK(
                    appId,
                    apkId,
                    formData,
                    makeTokenFileConfig(token, file.name, onUploadProgress),
                )
                    .then(() =>
                        navigate(
                            generatePath(APP_ROUTES.APP_DETAILS, { id: appId }),
                        ),
                    )
                    .catch(e => handleServerAPIError(e, dispatch, navigate))
                    .finally(() => {
                        dispatch(setIsLoading(false))
                        dispatch(setLoadingProgress(-1))
                    })
            }
        },
        [appId, apkId, file, spaceInfo, dispatch, navigate, token, onUploadProgress],
    )

    return (
        <Modal show={isOpen} onHide={toggle} style={{ zIndex: '9000' }}>
            <ModalHeader>
                <ModalTitle>Upload APK</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={onUpload}>
                    <FormGroup className="mb-3">
                        <FormLabel>Upload APK</FormLabel>
                        <FormControl
                            type="file"
                            accept=".apk, .xapk, .apks"
                            onChange={async (
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                                const selected = e.target.files?.[0]
                                setFile(selected)
                                setSpaceInfo(null)

                                if (!selected || !token) return

                                try {
                                    setSpaceLoading(true)
                                    const res = await checkUploadAvailability(
                                        selected.size,
                                        makeTokenHeaderConfig(token),
                                    )
                                    setSpaceInfo(res.data)
                                } catch {
                                    setSpaceInfo(null)
                                } finally {
                                    setSpaceLoading(false)
                                }
                            }}
                        />
                    </FormGroup>

                    {file && (
                        <div className="mt-3 p-3 border rounded bg-light">
                            <div>
                                <strong>File size:</strong>{' '}
                                {formatBytes(file.size)}
                            </div>

                            {spaceLoading && (
                                <div className="text-muted mt-2">
                                    Checking server space...
                                </div>
                            )}

                            {!spaceLoading && spaceInfo && (
                                <>
                                    <div>
                                        <strong>Total space:</strong>{' '}
                                        {formatBytes(spaceInfo.totalBytes)}
                                    </div>
                                    <div>
                                        <strong>Free space:</strong>{' '}
                                        {formatBytes(spaceInfo.availableBytes)}
                                    </div>
                                    <div className="mt-2">
                                        <strong>Status:</strong>{' '}
                                        <span
                                            className={
                                                spaceInfo.canUpload
                                                    ? 'text-success'
                                                    : 'text-danger'
                                            }>
                                            {spaceInfo.canUpload
                                                ? 'Can upload'
                                                : 'Not enough disk space'}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <Button
                        className="mt-3"
                        disabled={
                            !file || !spaceInfo?.canUpload || spaceLoading
                        }
                        type="submit">
                        Upload
                    </Button>
                </Form>
            </ModalBody>
        </Modal>
    )
}